import { useMemo } from 'react'
import { useRouter } from 'next/router'
import moment from 'moment'
import useTranslation from 'next-translate/useTranslation'
import { OrderType, PreOrderStatus } from '@ancon/wildcat-types'
import LinkIcon from '@ancon/wildcat-ui/shared/icons/link-outline.svg'
import CheckIcon from '@ancon/wildcat-ui/shared/icons/check-circle-fill.svg'

import Modal from '../../../app/components/modal/Modal'
import Button from '../../../app/components/Button'
import Input from '../../../app/components/input/Input'
import WhatsNext from '../../../app/components/whatsNext/WhatsNext'
import useClipboard from '../../../app/hooks/useClipboard'
import useGetOrderFormatName from '../../../app/hooks/useGetOrderFormatName'
import useIsRouteChanging from '../../../app/hooks/useIsRouteChanging'
import { AppRoutes } from '../../../app/constants'
import getFormattedOrderTimeSlot from '../../../outlet/utils/getFormattedOrderTimeSlot'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { clientContextCurrentUserCompanyNameSelector } from '../../../clientContext/store/clientContextSelectors'
import { outletsSelectedOutletListItemNameSelector } from '../../../outlets/store/outletsSelectors'
import CompanyTag from '../../../company/components/CompanyTag'
import {
  preOrderCreatedGroupOrderIdSelector,
  preOrderNewGroupOrderNameSelector,
  preOrderNewOrderOrderFormatSelector,
  preOrderNewOrderPanelSelector,
  preOrderNewOrderTimeSelector,
} from '../../store/preOrderSelectors'
import {
  resetNewOrderDetails,
  setNewOrderPanelCreatedGroupOrderStatus,
} from '../../store/preOrderSlice'
import createPreOrderSharableLink from '../../utils/createPreOrderSharableLink'
import useStartGroupOrderHostSession from '../../hooks/useStartGroupOrderHostSession'

import styles from './GroupOrderCreatedModal.module.scss'

/**
 * Confirms a group order made in the new order panel. Activated: the link to
 * share, what happens next and where to go. Approval pending: what happens
 * before there is a link to share.
 */
export default function GroupOrderCreatedModal() {
  const { t, lang } = useTranslation('preOrder')
  const dispatch = useAppDispatch()
  const router = useRouter()
  const clipboard = useClipboard()
  const isRouteChanging = useIsRouteChanging()
  const getOrderFormatName = useGetOrderFormatName()
  const startHostSession = useStartGroupOrderHostSession()

  const { createdGroupOrderStatus } = useAppSelector(
    preOrderNewOrderPanelSelector,
  )
  const createdGroupOrderId = useAppSelector(
    preOrderCreatedGroupOrderIdSelector,
  )
  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )
  const outletName = useAppSelector(outletsSelectedOutletListItemNameSelector)
  const groupOrderName = useAppSelector(preOrderNewGroupOrderNameSelector)
  const orderFormat = useAppSelector(preOrderNewOrderOrderFormatSelector)
  const orderTime = useAppSelector(preOrderNewOrderTimeSelector)

  const isOpen = createdGroupOrderStatus !== null
  const isActivated = createdGroupOrderStatus === PreOrderStatus.Activated

  const groupOrderLink = useMemo(
    () =>
      createdGroupOrderId
        ? createPreOrderSharableLink(
            createdGroupOrderId,
            OrderType.GroupPreOrder,
            lang,
          )
        : '',
    [createdGroupOrderId, lang],
  )

  const orderWhen = orderTime
    ? `${moment(orderTime).format('ddd D MMM')}, ${getFormattedOrderTimeSlot(orderTime)}`
    : ''

  function handleClose() {
    dispatch(setNewOrderPanelCreatedGroupOrderStatus(null))
    dispatch(resetNewOrderDetails())
  }

  async function handleManageMembers() {
    if (startHostSession()) {
      await router.push(AppRoutes.PreOrderManage)
    }

    handleClose()
  }

  const summaryRows = [
    {
      label: t('groupOrderCreated.for'),
      value: companyName && (
        <CompanyTag>
          {t('common:identity.companySection', { companyName })}
        </CompanyTag>
      ),
    },
    { label: t('groupOrderCreated.restaurant'), value: outletName },
    {
      label: t('groupOrderCreated.when'),
      value:
        orderFormat != null
          ? `${getOrderFormatName(orderFormat)} · ${orderWhen}`
          : orderWhen,
    },
  ].filter(row => !!row.value)

  const steps = isActivated
    ? [
        t('groupOrderCreated.activatedSteps.share'),
        t('groupOrderCreated.activatedSteps.members'),
        t('groupOrderCreated.activatedSteps.place', {
          time: orderWhen,
          companyName,
        }),
      ]
    : [
        t('groupOrderCreated.pendingSteps.review'),
        t('groupOrderCreated.pendingSteps.email'),
        t('groupOrderCreated.pendingSteps.team'),
      ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      mobileModalMode="bottom-sheet"
      title={
        isActivated
          ? t('groupOrderCreated.activatedTitle')
          : t('groupOrderCreated.pendingTitle')
      }
      subtitle={groupOrderName}
      footer={
        isActivated ? (
          <>
            <Button
              size="large"
              variant="secondary"
              className={styles.footerButton}
              onClick={() => window.open(groupOrderLink, '_blank', 'noopener')}
            >
              {t('groupOrderCreated.previewAttendeeView')}
            </Button>
            <Button
              size="large"
              className={styles.footerButton}
              onClick={handleManageMembers}
              loading={isRouteChanging}
            >
              {t('groupOrderCreated.manageMembers')}
            </Button>
          </>
        ) : (
          <Button
            size="large"
            className={styles.footerButton}
            onClick={handleClose}
          >
            {t('newOrder.done')}
          </Button>
        )
      }
    >
      <div className={styles.body}>
        {isActivated && (
          <div className={styles.link}>
            <Input
              readOnly
              value={groupOrderLink}
              aria-label={t('groupOrderCreated.link')}
              leftAccessory={<LinkIcon />}
              containerClassName={styles.linkInput}
            />
            <Button
              variant="secondary"
              onClick={() => clipboard.copyText(groupOrderLink)}
            >
              {clipboard.isCopied ? t('copied') : t('copyLink')}
            </Button>
          </div>
        )}
        <WhatsNext
          icon={isActivated ? <CheckIcon /> : undefined}
          summaryRows={summaryRows}
          steps={steps}
        />
      </div>
    </Modal>
  )
}
